import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Layers, Target } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Select } from '../../../../../components/ui/Select';
import { GROUPS, type GroupId } from '../../../../../config/systemConfig';
import type { PracticeContentHealthReport, PracticeCapacityStatus } from '../hooks/useGovernanceData';
interface PracticeContentHealthTabProps {
  report: PracticeContentHealthReport;
  isRunning: boolean;
}
const STATUS_META: Record<PracticeCapacityStatus, {
  label: string;
  className: string;
}> = {
  critical: {
    label: 'Kritiek',
    className: 'bg-red-50 text-red-700 border-red-200'
  },
  thin: {
    label: 'Dun',
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  healthy: {
    label: 'Gezond',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
export function PracticeContentHealthTab({
  report,
  isRunning
}: PracticeContentHealthTabProps) {
  const [groupFilter, setGroupFilter] = useState<'all' | GroupId>('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const modules = useMemo(() => Array.from(new Set(report.modules.map(item => item.module))).sort(), [report.modules]);
  const visibleFamilies = report.families.filter(family => {
    if (groupFilter !== 'all' && family.group !== groupFilter) return false;
    return moduleFilter === 'all' || family.module === moduleFilter;
  });
  const visibleModules = report.modules.filter(item => {
    if (groupFilter !== 'all' && item.group !== groupFilter) return false;
    return moduleFilter === 'all' || item.module === moduleFilter;
  });
  if (isRunning && report.modules.length === 0) {
    return <div className="p-8 text-sm text-gray-500" data-id="element-5368">Oefenstof analyseren…</div>;
  }
  return <div className="space-y-6" data-id="element-5369">
      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-id="element-5370">
        <div className="flex items-start gap-4" data-id="element-5371">
          <div className="rounded-xl bg-indigo-100 p-3 text-indigo-700" data-id="element-5372">
            <Layers className="h-6 w-6" data-id="element-5373" />
          </div>
          <div data-id="element-5374">
            <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-700" data-id="element-5375">
              Practice capacity
            </p>
            <h2 className="mt-1 text-xl font-bold text-slate-900" data-id="element-5376">
              Oefenstof-capaciteit
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600" data-id="element-5377">
              Waar kan adaptief oefenen nog veilig variëren? Dit meet bruikbare,
              unieke vragen per family binnen de tiers van een groep — geen
              mastery-score en geen blokkade voor leerlingen.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-5378">
        <Metric label="Kritieke families" value={report.counts.critical} tone="red" data-id="element-5379" />
        <Metric label="Dunne families" value={report.counts.thin} tone="amber" data-id="element-5380" />
        <Metric label="Gezonde families" value={report.counts.healthy} tone="emerald" data-id="element-5381" />
      </div>

      <Card className="p-4" data-id="element-5382">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-5383">
          <Select value={String(groupFilter)} onChange={event => setGroupFilter(event.target.value === 'all' ? 'all' : Number(event.target.value) as GroupId)} options={[{
          value: 'all',
          label: 'Alle groepen'
        }, ...GROUPS.map(group => ({
          value: String(group),
          label: `Groep ${group}`
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-5384" />
          <Select value={moduleFilter} onChange={event => setModuleFilter(event.target.value)} options={[{
          value: 'all',
          label: 'Alle modules'
        }, ...modules.map(module => ({
          value: module,
          label: module
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-5385" />
          <p className="text-xs text-slate-500 sm:ml-auto" data-id="element-5386">
            Kritiek: 0–3 vragen · dun: 4–6 · gezond: 7+
          </p>
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-5387">
        <div className="border-b border-slate-100 p-5" data-id="element-5388">
          <h3 className="text-sm font-semibold text-slate-900" data-id="element-5389">
            Praktijkruimte per groep × module
          </h3>
          <p className="mt-1 text-xs text-slate-500" data-id="element-5390">
            Rode combinaties bevatten families die direct tot fallback,
            herhaling of leerdoel-drift kunnen leiden.
          </p>
        </div>
        <div className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3" data-id="element-5391">
          {visibleModules.map(item => {
          const meta = STATUS_META[item.status];
          return <div key={`${item.group}-${item.module}`} className="bg-white p-4" data-id="element-5392">
                <div className="flex items-center justify-between gap-2" data-id="element-5393">
                  <p className="font-mono text-xs font-bold text-slate-800" data-id="element-5394">
                    G{item.group} · {item.module}
                  </p>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.className}`} data-id="element-5395">
                    {meta.label}
                  </span>
                </div>
                <p className="mt-3 text-2xl font-bold tabular-nums text-slate-900" data-id="element-5396">
                  {item.totalQuestions}
                </p>
                <p className="text-xs text-slate-500" data-id="element-5397">
                  vragen · {item.familiesWithContent}/{item.eligibleFamilies}{' '}
                  families met inhoud
                </p>
                <p className="mt-2 text-xs text-slate-600" data-id="element-5398">
                  {item.criticalFamilies} kritiek · {item.thinFamilies} dun
                </p>
              </div>;
        })}
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-5399">
        <div className="flex items-start gap-3 border-b border-slate-100 p-5" data-id="element-5400">
          <Target className="mt-0.5 h-4 w-4 text-red-500" data-id="element-5401" />
          <div data-id="element-5402">
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-5403">
              Auteurprioriteit
            </h3>
            <p className="mt-1 text-xs text-slate-500" data-id="element-5404">
              Kritiek vraagt eerst om inhoud. Dunne families zijn daarna
              uitbreidingswerk: zeven unieke varianten is de minimumbasis; een
              gerichte sessie van tien vragen vraagt nog steeds om herhaling.
            </p>
          </div>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-5405">
          {visibleFamilies.filter(family => family.status !== 'healthy').map(family => {
          const meta = STATUS_META[family.status];
          return <div key={family.key} className="flex items-center gap-3 p-4" data-id="element-5406">
                  {family.status === 'critical' ? <AlertTriangle className="h-4 w-4 flex-none text-red-500" data-id="element-5407" /> : <AlertTriangle className="h-4 w-4 flex-none text-amber-500" data-id="element-5408" />}
                  <div className="min-w-0 flex-1" data-id="element-5409">
                    <p className="font-mono text-xs font-semibold text-slate-900" data-id="element-5410">
                      G{family.group} · {family.familyId}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-5411">
                      {family.questionCount} bruikbare vragen
                      {family.questionTypes.length > 0 && ` · ${family.questionTypes.join(', ')}`}
                    </p>
                    {family.sessionRepeatCount > 0 && <p className="mt-1 text-xs text-slate-500" data-id="element-5412">
                        In een focusset van 10: minimaal{' '}
                        {family.sessionRepeatCount} herhaling
                        {family.sessionRepeatCount === 1 ? '' : 'en'} zonder
                        nieuwe varianten
                      </p>}
                  </div>
                  <span className={`rounded-full border px-2 py-1 text-[10px] font-bold ${meta.className}`} data-id="element-5413">
                    +{family.questionsNeeded} naar minimum
                  </span>
                </div>;
        })}
          {visibleFamilies.every(family => family.status === 'healthy') && <div className="flex items-center gap-2 p-6 text-sm text-emerald-700" data-id="element-5414">
              <CheckCircle2 className="h-5 w-5" data-id="element-5415" />
              Geen dunne of kritieke families binnen deze selectie.
            </div>}
        </div>
      </Card>
    </div>;
}
function Metric({
  label,
  value,
  tone
}: {
  label: string;
  value: number;
  tone: 'red' | 'amber' | 'emerald';
}) {
  const colors = {
    red: 'border-red-100 bg-red-50 text-red-700',
    amber: 'border-amber-100 bg-amber-50 text-amber-700',
    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700'
  }[tone];
  return <div className={`rounded-xl border p-4 ${colors}`} data-id="element-5416">
      <p className="text-2xl font-bold tabular-nums" data-id="element-5417">{value}</p>
      <p className="mt-1 text-xs font-medium" data-id="element-5418">{label}</p>
    </div>;
}